import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  styled,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Link as MUILink,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import GitHubIcon from "@mui/icons-material/GitHub";
import GoogleIcon from "@mui/icons-material/Google";
import { signInWithGithub, signInWithGoogle } from "./OAuth";

// Layout
const AuthContainer = styled("div")(() => ({
  display: "flex",
  height: "100vh",
}));

const Panel = styled("div")(({ theme, bgColor, textColor }) => ({
  flex: 1,
  backgroundColor: bgColor || theme.palette.background.paper,
  color: textColor || theme.palette.text.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  padding: "2rem",
  boxSizing: "border-box",
}));

const FormBox = styled(Box)(() => ({
  width: "100%",
  maxWidth: 400,
}));

const AuthTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.5rem",
  fontWeight: "bold",
  marginBottom: theme.spacing(4),
  textAlign: "center",
}));

const AuthQuestion = styled("p")(({ theme }) => ({
  margin: theme.spacing(2, 0),
  fontSize: "1rem",
  textAlign: "center",
}));

const Login = ({ onLogin }) => {
  const navigate = useNavigate();

  // sign-in state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loadingPwd, setLoadingPwd] = useState(false);
  const [loadingGuest, setLoadingGuest] = useState(false);

  // reset-password dialog state (these were missing)
  const [resetEmail, setResetEmail] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState("");
  const [resetError, setResetError] = useState("");

  // helper: clear guest and notify app
  const clearGuestFlag = () => {
    localStorage.removeItem("guest");
    window.dispatchEvent(new Event("guest-mode-change"));
  };

  // helper: set guest and notify app
  const setGuestFlag = () => {
    localStorage.setItem("guest", "1");
    window.dispatchEvent(new Event("guest-mode-change"));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoadingPwd(true); // you weren’t setting this before

    try {
      const { data, error: signInError } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });

      if (signInError) throw signInError; // check the right var
      clearGuestFlag();

      // let App.js auth listener update user; optionally still call onLogin
      onLogin?.(data?.user || null);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Sign in failed.");
    } finally {
      setLoadingPwd(false);
    }
  };

  const handleOAuthError = (err) => {
    setError(err.message || "OAuth sign-in failed.");
  };

  const handleSignInWithGithub = async () => {
    try {
      clearGuestFlag();
      await signInWithGithub();
      // Supabase will redirect back; App.js listener picks up session
    } catch (err) {
      handleOAuthError(err);
    }
  };

  const handleSignInWithGoogle = async () => {
    try {
      clearGuestFlag();
      await signInWithGoogle();
    } catch (err) {
      handleOAuthError(err);
    }
  };

  // Forgot password handlers
  const openReset = () => {
    setResetEmail(email || "");
    setResetError("");
    setResetSuccess("");
    setResetOpen(true);
  };
  const closeReset = () => {
    setResetOpen(false);
    setResetLoading(false);
    setResetError("");
    setResetSuccess("");
  };

  const sendResetLink = async () => {
    setResetLoading(true);
    setResetError("");
    setResetSuccess("");

    try {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1");

      const redirectTo = isLocal
        ? "http://localhost:3000/reset-password"
        : "https://paper-pulse-drab.vercel.app/reset-password";

      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(
        resetEmail.trim(),
        { redirectTo }
      );
      if (resetErr) throw resetErr;

      setResetSuccess(
        "If an account exists for that email, a reset link has been sent."
      );
    } catch (err) {
      setResetError(err.message || "Could not send reset email");
    } finally {
      setResetLoading(false);
    }
  };

  // Guest mode (no Supabase session)
  const handleGuestMode = async () => {
    setError("");
    setLoadingGuest(true);
    try {
      setGuestFlag();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Unable to start guest session.");
    } finally {
      setLoadingGuest(false);
    }
  };

  return (
    <AuthContainer>
      <Panel>
        <FormBox component="form" onSubmit={handleSubmit}>
          <AuthTitle>Login to AISecurityPulse</AuthTitle>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loadingPwd || loadingGuest}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loadingPwd || loadingGuest}
          />

          {/* Clear, visible "Forgot your password?" link */}
          <MUILink
            component="button"
            type="button"
            onClick={openReset}
            underline="hover"
            sx={{
              fontSize: 14,
              mt: 1,
              display: "block",
              textAlign: "right",
              color: "primary.main",
              fontWeight: 500,
            }}
          >
            Forgot your password?
          </MUILink>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 1 }}
            disabled={loadingPwd || loadingGuest}
          >
            {loadingPwd ? "Signing in..." : "Sign In"}
          </Button>

          <AuthQuestion>Or sign in with</AuthQuestion>
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 2 }}
          >
            <IconButton
              onClick={handleSignInWithGithub}
              disabled={loadingPwd || loadingGuest}
            >
              <GitHubIcon />
            </IconButton>
            <IconButton
              onClick={handleSignInWithGoogle}
              disabled={loadingPwd || loadingGuest}
            >
              <GoogleIcon />
            </IconButton>
          </Box>

          <Divider sx={{ my: 3 }}>or</Divider>

          {/* Guest mode */}
          <Button
            fullWidth
            variant="outlined"
            onClick={handleGuestMode}
            disabled={loadingGuest || loadingPwd}
          >
            {loadingGuest ? "Starting guest session..." : "Continue as Guest"}
          </Button>

          <AuthQuestion>
            Don&apos;t have an account? <Link to="/register">Sign Up</Link>
          </AuthQuestion>
        </FormBox>
      </Panel>

      <Panel bgColor="#000000" textColor="#ffffff">
        <Typography variant="h3" sx={{ textAlign: "center" }}>
          Welcome to AISecurityPulse
        </Typography>
      </Panel>

      {/* Forgot Password Dialog */}
      <Dialog open={resetOpen} onClose={closeReset} fullWidth maxWidth="xs">
        <DialogTitle>Password reset</DialogTitle>
        <DialogContent dividers>
          {resetError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetError}
            </Alert>
          )}
          {resetSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {resetSuccess}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the email associated with your account. We’ll send you a link
            to reset your password.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="reset-email"
            label="Email address"
            type="email"
            fullWidth
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReset} disabled={resetLoading}>
            Cancel
          </Button>
          <Button
            onClick={sendResetLink}
            variant="contained"
            disabled={resetLoading || !resetEmail.trim()}
            startIcon={resetLoading ? <CircularProgress size={18} /> : null}
          >
            {resetLoading ? "Sending…" : "Send reset link"}
          </Button>
        </DialogActions>
      </Dialog>
    </AuthContainer>
  );
};

export default Login;
